import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { addBatch } from './useBatchOps'

export type Location = 'fridge' | 'freezer' | 'pantry' | 'other'

export type FinishUnpackInput = {
  shoppingListId: string
  householdId:    string
  // Map of list_items.id → confirmed? (true = bought, false = skipped)
  // Each entry must include the corresponding pantry_items.id so we can log a confirmation row.
  // location/expiryDate are applied to the canonical pantry_items row when confirmed.
  confirmations:  Array<{
    listItemId:    string
    pantryItemId:  string
    confirmed:     boolean
    location?:     Location
    expiryDate?:   string | null
    tracksExpiry?: boolean
    // Per-item price + store override from receipt-scan match. When unset,
    // we fall back to the split-totalSpent / count fallback below.
    pricePaid?:    number | null
    storeName?:    string | null
    // Quantity bought (defaults to 1 if missing). Becomes the new batch's
    // quantity in the on-list path so receipt-scan matches don't lose count.
    quantity?:     number | null
  }>
  impulseItems:   Array<{
    name:          string
    quantity?:     number | null
    quantityUnit?: string
    price?:        number | null
    category?:     string
    location?:     Location
    tracksExpiry?: boolean
    expiryDate?:   string | null
  }>
  totalSpent:     number | null
  storeName:      string | null
}

export function useFinishUnpack() {
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState<string | null>(null)

  async function finish(input: FinishUnpackInput): Promise<boolean> {
    setLoading(true)
    setError(null)

    try {
      const now = new Date().toISOString()

      const checkedIds    = input.confirmations.filter(c =>  c.confirmed).map(c => c.listItemId)
      const uncheckedIds  = input.confirmations.filter(c => !c.confirmed).map(c => c.listItemId)
      const confirmedItems = input.confirmations.filter(c => c.confirmed)

      // 1. Toggle list_items.status for each confirmation
      if (checkedIds.length > 0) {
        const { error } = await supabase
          .from('list_items')
          .update({ status: 'checked', checked_at: now })
          .in('id', checkedIds)
        if (error) throw error
      }
      if (uncheckedIds.length > 0) {
        const { error } = await supabase
          .from('list_items')
          .update({ status: 'pending', checked_at: null })
          .in('id', uncheckedIds)
          .eq('status', 'checked')  // only flip back items that were checked; leave 'removed' alone
        if (error) throw error
      }

      // 2. Insert confirmations (transactional log) for each on-list item.
      //    Per-item price wins when the receipt scan attached one to the row;
      //    otherwise we fall back to splitting input.totalSpent across the
      //    confirmed items so partial info is still useful.
      if (confirmedItems.length > 0) {
        const fallbackPerItem = input.totalSpent != null && input.totalSpent > 0
          ? Number((input.totalSpent / confirmedItems.length).toFixed(2))
          : null

        const { data: confRows, error } = await supabase
          .from('confirmations')
          .insert(
            confirmedItems.map(c => {
              const price     = c.pricePaid ?? fallbackPerItem
              const storeName = c.storeName?.trim() || input.storeName
              return {
                item_id:      c.pantryItemId,
                household_id: input.householdId,
                confirmed_at: now,
                source:       'unpack',
                quantity:     1,
                price_paid:   price,
                total_paid:   price,
                store_name:   storeName,
              }
            }),
          )
          .select('id, item_id, price_paid, store_name')
        if (error) throw error

        // Mirror into store_prices using each row's actual per-item price +
        // store. Skip rows missing either (NOT NULL constraints + analytics
        // hygiene). store_prices is what Spending / Savings / List /
        // StorePrices and ItemDetailSheet read from.
        const priceLogRows = (confRows ?? [])
          .filter(c => c.price_paid != null && Number(c.price_paid) > 0 && c.store_name?.trim())
          .map(c => ({
            item_id:         c.item_id,
            household_id:    input.householdId,
            store_name:      c.store_name!.trim(),
            price:           c.price_paid as number,
            recorded_at:     now,
            confirmation_id: c.id,
          }))

        if (priceLogRows.length > 0) {
          const { error: priceErr } = await supabase
            .from('store_prices')
            .insert(priceLogRows)
          if (priceErr) {
            console.warn('[useFinishUnpack] store_prices log failed (on-list):', priceErr)
          }
        }

        // 3. Update each confirmed pantry_item with non-quantity metadata.
        //    NOTE: current_quantity and current_expiration_date are NOT
        //    written here — the Phase 1 trigger derives them from item_batches
        //    after we insert the new batches in step 4. Only fields the trigger
        //    can't compute (location, track_expiration toggle, last_confirmed_at)
        //    live in this patch.
        for (const c of confirmedItems) {
          const patch: Record<string, unknown> = {
            last_confirmed_at: now,
            status_override:   null,
          }
          if (c.location) patch.location = c.location
          if (c.tracksExpiry !== undefined) {
            patch.track_expiration = !!c.tracksExpiry
          } else if (c.expiryDate) {
            patch.track_expiration = true
          }

          const { error: piErr } = await supabase
            .from('pantry_items')
            .update(patch)
            .eq('id', c.pantryItemId)
          if (piErr) {
            console.warn('[useFinishUnpack] pantry_item update failed:', piErr, c)
          }
        }

        // 4. Create one item_batches row per confirmed item. The trigger then
        //    recomputes pantry_items.current_quantity and
        //    current_expiration_date from active batches.
        //    Receipt-match expiry (per item) wins; otherwise no expiry on the
        //    new batch (existing batches still carry their own expiries, so the
        //    trigger picks the earliest across all of them).
        //    quantity column is INTEGER per the schema — round at the boundary.
        const fallbackStore = input.storeName?.trim() || null
        const batchRows = confirmedItems.map(c => {
          const useExpiry = c.tracksExpiry !== false
          return {
            item_id:      c.pantryItemId,
            household_id: input.householdId,
            quantity:     Math.max(1, Math.round(c.quantity ?? 1)),
            expiry_date:  useExpiry ? (c.expiryDate ?? null) : null,
            status:       'active',
            store_name:   c.storeName?.trim() || fallbackStore,
            source:       'unpack',
            purchased_at: now,
          }
        })

        if (batchRows.length > 0) {
          const { error: batchErr } = await supabase
            .from('item_batches')
            .insert(batchRows)
          if (batchErr) {
            console.warn('[useFinishUnpack] batch insert (on-list) failed:', batchErr)
          }
        }
      }

      // 4. Impulse items: create or increment a pantry_item, link via
      //    list_items with is_impulse_buy=true, log confirmation.
      //
      //    Conservative duplicate-check: case-insensitive exact-name match
      //    on active items. "Greek Yogurt" matches "greek yogurt" but NOT
      //    "Plain Greek Yogurt". When matched, we add to current_quantity
      //    instead of creating a new row — prevents pile-up when a user
      //    re-scans an item they already track. Latest expiry/location wins.
      for (const impulse of input.impulseItems) {
        const trimmedName = impulse.name.trim()
        if (!trimmedName) continue

        const tracksExpiry = !!impulse.tracksExpiry
        const expiryDate   = impulse.expiryDate || null
        const qty          = impulse.quantity && impulse.quantity > 0 ? impulse.quantity : 1
        const totalPaid    = impulse.price ?? null
        const unitPrice    = totalPaid != null && qty > 0 ? Number((totalPaid / qty).toFixed(2)) : null
        const unit         = impulse.quantityUnit && impulse.quantityUnit.trim() ? impulse.quantityUnit : 'count'

        // Conservative duplicate check
        const { data: existingMatches, error: lookupErr } = await supabase
          .from('pantry_items')
          .select('id, current_quantity')
          .eq('household_id', input.householdId)
          .eq('is_active', true)
          .ilike('name', trimmedName)
          .limit(1)

        if (lookupErr) {
          console.warn('[useFinishUnpack] impulse lookup failed, falling through to insert:', lookupErr)
        }

        let pantryItemId: string

        if (existingMatches && existingMatches.length > 0) {
          // Match found — append a NEW BATCH for this purchase instead of
          // incrementing current_quantity directly. Each scan stays auditable
          // as its own batch with its own expiry. The trigger keeps the
          // cached current_quantity in sync.
          const existing = existingMatches[0]
          pantryItemId   = existing.id

          // Update non-quantity metadata (latest location, last_confirmed)
          const metaPatch: Record<string, unknown> = {
            last_confirmed_at: now,
            is_active:         true,
          }
          if (impulse.location) metaPatch.location = impulse.location
          if (tracksExpiry)     metaPatch.track_expiration = true

          const { error: metaErr } = await supabase
            .from('pantry_items')
            .update(metaPatch)
            .eq('id', existing.id)
            .eq('household_id', input.householdId)
          if (metaErr) throw metaErr

          await addBatch({
            itemId:      existing.id,
            householdId: input.householdId,
            quantity:    qty,
            expiryDate:  tracksExpiry ? expiryDate : null,
            purchasedAt: now,
            source:      'unpack',
            storeName:   input.storeName?.trim() || null,
          })
        } else {
          // No match — create a new pantry_item, then add its first batch.
          // current_quantity is left out of the insert; the trigger will fill
          // it in when we add the batch in the next step.
          const { data: newItem, error: itemErr } = await supabase
            .from('pantry_items')
            .insert({
              household_id:            input.householdId,
              name:                    trimmedName,
              display_category:        impulse.category ?? 'other',
              location:                impulse.location ?? 'pantry',
              quantity_unit:           unit,
              is_active:               true,
              confirmation_count:      1,
              last_confirmed_at:       now,
              track_expiration:        tracksExpiry,
            })
            .select('id')
            .single()

          if (itemErr || !newItem) throw itemErr ?? new Error('Failed to create impulse item')
          pantryItemId = newItem.id

          await addBatch({
            itemId:      newItem.id,
            householdId: input.householdId,
            quantity:    qty,
            expiryDate:  tracksExpiry ? expiryDate : null,
            purchasedAt: now,
            source:      'unpack',
            storeName:   input.storeName?.trim() || null,
          })
        }

        const baseLink = {
          list_id:       input.shoppingListId,
          item_id:       pantryItemId,
          status:        'checked',
          urgency_score: 50,
          sort_order:    999,
          is_one_off:    true,
          checked_at:    now,
        }

        let { error: linkErr } = await supabase
          .from('list_items')
          .insert({ ...baseLink, is_impulse_buy: true })

        // Fall back if the unpack_spend_columns migration hasn't been pushed
        // to the live DB. Item still gets recorded, just without the impulse flag.
        if (linkErr && /is_impulse_buy/i.test(linkErr.message ?? '')) {
          console.warn(
            '[useFinishUnpack] is_impulse_buy column missing — inserting without the flag. ' +
            'Apply supabase/migrations/20260423180000_unpack_spend_columns.sql to fix.'
          )
          ;({ error: linkErr } = await supabase
            .from('list_items')
            .insert(baseLink))
        }
        if (linkErr) throw linkErr

        const { data: confRow, error: confErr } = await supabase
          .from('confirmations')
          .insert({
            item_id:      pantryItemId,
            household_id: input.householdId,
            confirmed_at: now,
            source:       'unpack',
            quantity:     qty,
            price_paid:   unitPrice,
            total_paid:   totalPaid,
            store_name:   input.storeName,
          })
          .select('id')
          .single()

        // Don't fail the whole unpack on a confirmation-log hiccup. The
        // pantry_item exists, the list_items link exists; missing
        // confirmation history is recoverable, a thrown error here would
        // leave the user with a partial unpack and no clear retry path.
        if (confErr) {
          console.warn('[useFinishUnpack] confirmation insert failed (impulse):', confErr)
        }

        // Log the per-unit price to store_prices for analytics. unitPrice
        // (not totalPaid) is what comparison surfaces want — even if the
        // user bought 3 cans, the comparable value across stores is per-can.
        const impulseStore = input.storeName?.trim()
        if (unitPrice != null && unitPrice > 0 && impulseStore) {
          // Receipt-v2 parser may attach a `savings` field per impulse; cast
          // protects against the type not yet declaring it. When the field
          // lands properly on the type, drop the cast.
          const wasOnSale = Boolean(
            (impulse as { savings?: number | null }).savings != null
              && (impulse as { savings?: number | null }).savings! > 0
          )

          const { error: priceErr } = await supabase
            .from('store_prices')
            .insert({
              item_id:         pantryItemId,
              household_id:    input.householdId,
              store_name:      impulseStore,
              price:           unitPrice,
              recorded_at:     now,
              confirmation_id: confRow?.id ?? null,
              was_on_sale:     wasOnSale,
            })
          if (priceErr) {
            console.warn('[useFinishUnpack] store_prices log failed (impulse):', priceErr)
          }
        }
      }

      // 5. Mark the list as fully done (shopped + unpacked) and stamp totals.
      // unpacked_at is the new dedicated marker that retires the list from
      // usePendingUnpack; completed_at is kept in lockstep so the 14 other
      // consumers reading completed_at IS NULL continue to behave correctly.
      // If unpacked_at column hasn't been migrated yet, fall back to writing
      // just completed_at so the unpack still finishes.
      const finalPatch = {
        completed_at: now,
        unpacked_at:  now,
        total_spent:  input.totalSpent,
        store_name:   input.storeName,
      }

      let finErr: any = null
      ;({ error: finErr } = await supabase
        .from('shopping_lists')
        .update(finalPatch)
        .eq('id', input.shoppingListId))

      if (finErr && /unpacked_at/i.test(finErr.message ?? '')) {
        console.warn(
          '[useFinishUnpack] unpacked_at column missing — writing completed_at only. ' +
          'Apply supabase/migrations/20260425120000_add_unpacked_at_to_shopping_lists.sql.'
        )
        const { unpacked_at: _unused, ...legacy } = finalPatch
        ;({ error: finErr } = await supabase
          .from('shopping_lists')
          .update(legacy)
          .eq('id', input.shoppingListId))
      }
      if (finErr) throw finErr

      setLoading(false)
      return true
    } catch (err: any) {
      console.error('[useFinishUnpack]', err)
      setError(err?.message ?? 'Failed to finish unpack')
      setLoading(false)
      return false
    }
  }

  return { finish, loading, error }
}
